import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";

// Тур по админке терминологии на driver.js. Билдеры шагов вынесены как
// чистые функции (без DOM) — их покрывают unit-тесты; runTour() уже трогает
// DOM и вызывается из обработчиков кнопок «? Тур».
//
// Шаги ссылаются на элементы по атрибутам data-tour="…", которые
// проставлены в AdminTerminology.tsx и AdminTerminologyItem.tsx. Если
// элемента нет на странице, driver.js покажет шаг по центру как модалку.

export type TourStep = DriveStep;

export function buildTerminologyListTourSteps(): TourStep[] {
  return [
    {
      element: '[data-tour="term-type"]',
      popover: {
        title: "Тип записи",
        description:
          "Выберите, что ищете: термин, аббревиатуру или alias " +
          "(запрещённую/синонимичную форму).",
      },
    },
    {
      element: '[data-tour="term-search"]',
      popover: {
        title: "Поиск",
        description:
          "Введите термин, alias или источник и нажмите «Найти». Слева " +
          "можно дофильтровать по домену, источнику и статусу.",
      },
    },
    {
      element: '[data-tour="term-table"]',
      popover: {
        title: "Открыть запись",
        description:
          "Кликните по строке или по стрелке → справа, чтобы открыть " +
          "карточку. Принудительные замены настраиваются внутри карточки — " +
          "там есть своя кнопка «? Тур».",
      },
    },
  ];
}

export function buildTerminologyItemTourSteps(opts: {
  canHaveForbiddenForms: boolean;
  isReadOnly: boolean;
}): TourStep[] {
  if (opts.isReadOnly) {
    return [
      {
        element: '[data-tour="item-header"]',
        popover: {
          title: "Запись-кандидат",
          description:
            "Это кандидат из майнинга — напрямую не редактируется. Кнопкой " +
            "«Отправить в ревью» переведите его в очередь, одобрите — и он " +
            "попадёт в активный словарь.",
        },
      },
    ];
  }

  const steps: TourStep[] = [
    {
      element: '[data-tour="usage-block"]',
      popover: {
        title: "Шаг 1 — режим записи",
        description:
          "Бейдж справа показывает, как запись работает при переводе: " +
          "«Hint only» — только подсказка, без автозамены; «Hard enforcement» " +
          "(зелёный) — жёсткая подстановка этого перевода. Наша цель — сделать " +
          "его Hard.",
      },
    },
    {
      element: '[data-tour="usage-actions"]',
      popover: {
        title: "Шаг 2 — включить Hard enforcement",
        description:
          "Жми «Promote to hard» (или «Одобрить как hard», если запись ещё не " +
          "активна) — бейдж сверху станет зелёным «Hard enforcement», и " +
          "resolver начнёт жёстко подставлять этот перевод. Проверить можно " +
          "сразу на странице «Перевод». Это и есть весь путь до enforcement.",
      },
    },
  ];

  if (opts.canHaveForbiddenForms) {
    steps.push(
      {
        element: '[data-tour="forbidden-forms"]',
        popover: {
          title: "Запрещённые формы",
          description:
            "Сюда добавляют вариант, который переводчик выдаёт ошибочно " +
            "(например, «ППАК» вместо «ПОАК»). Resolver будет автоматически " +
            "менять его на канонический при переводе.",
        },
      },
      {
        element: '[data-tour="alias-input"]',
        popover: {
          title: "Впишите кривую форму",
          description:
            "Текст, который встречается в выводе MT и должен быть заменён. " +
            "Для аббревиатур ниже выбирают: полный перевод (склоняется через " +
            "pymorphy) или сокращение (точная подстановка).",
        },
      },
      {
        element: '[data-tour="alias-submit"]',
        popover: {
          title: "Добавьте и включите enforcement",
          description:
            "Нажмите «Добавить» — форма сохранится как replace-alias. Чтобы " +
            "замена реально применялась, убедитесь, что в блоке выше включён " +
            "режим «Hard enforcement».",
        },
      },
    );
  }

  return steps;
}

/** Возвращает true и помечает тур как показанный, если он ещё не
 *  запускался в этом браузере (по ключу в localStorage). Используется для
 *  автозапуска тура при первом заходе — повторно сам не открывается, только
 *  по кнопке «? Тур». При недоступном localStorage возвращает false. */
export function shouldAutoRunTour(key: string): boolean {
  try {
    if (localStorage.getItem(key) === "done") return false;
    localStorage.setItem(key, "done");
    return true;
  } catch {
    return false;
  }
}

export function runTour(steps: TourStep[]): void {
  if (steps.length === 0) return;
  const tour = driver({
    showProgress: true,
    allowClose: true,
    nextBtnText: "Далее",
    prevBtnText: "Назад",
    doneBtnText: "Готово",
    progressText: "{{current}} из {{total}}",
    steps,
  });
  tour.drive();
}
